import _ from 'lodash'
import { joinDice, resolveDice, type DiceExpr } from '~/assets/dice/diceEval'
import type { SpellData } from '~/assets/spells/spellData'

export type Ability = 'str' | 'dex' | 'con' | 'int' | 'wis' | 'cha'

export type AbilityScoreData = {
  str: DiceExpr
  dex: DiceExpr
  con: DiceExpr
  int: DiceExpr
  wis: DiceExpr
  cha: DiceExpr
  hide?: boolean
}

export type Spellcasting = {
  stat: Ability
  progression: 'innate' | 'warlock' | 'full' | 'half' | 'third'
  level?: DiceExpr
  spells: Record<string, string[]>
  bonusSaves?: DiceExpr
  bonusAttacks?: DiceExpr
  bonusSlots?: number[]
  extra?: string
}

type SpeedData = { walk?: number; fly?: number; swim?: number; climb?: number }
type SenseData = { dark?: number; true?: number; blind?: number; tremor?: number }
type LevelData = Record<string, number>

export type BaseStats = {
  title: string
  generalName?: string
  level: number | LevelData
  cr?: number | string
  prof?: number
  class?: string
  ac: number | string
  speed?: string | SpeedData
  hitdie?: string
  hitdice?: string
  bonushp?: number
  hp?: number
  initiative?: DiceExpr
  size: string
  abilities: AbilityScoreData
  saves?: string[]
  savesBonus?: DiceExpr
  skills?: string[]
  skillBonus?: DiceExpr
  expertise?: string[]
  resistances?: string | string[]
  immunities?: string | string[]
  vulnerabilities?: string | string[]
  conditions?: string | string[]
  languages?: string | string[]
  senses?: SenseData
  spellcasting?: Spellcasting | Spellcasting[]
  mods?: Record<string, number>
}

export type StatblockData = BaseStats & {
  nohp?: boolean
  noinitiative?: boolean
  alignment?: string
  type: string
  subtype?: string
  swarm?: boolean
  cr: number | string
  role?: string
  player?: boolean
  capacity?: string
  cargo?: string
}

export type PlayerSpellcastingData = {
  // Which class' spellcasting rules you are using. Supports all third-caster subclasses as well.
  // This determines your base spell list (except for the `wizard`, who uses the `spellbook` directly), spell casting ability, spell slot progression, and whether rituals show up regardless of preparation (only for `wizard`).
  class: string
  // These spells are always prepared, and do not count towards the `preparations` limit. NB: All spells are compared by exact match. `'Hunters Mark'` and `'Hunter\'s Mark'` are NOT equivalent - only the latter will fetch the correct data.
  always?: string[]
  // When `class=wizard`, this is the base list for preparations.
  // For other classes, these spells are added to the base list and made available for preparation.
  spellbook?: string[]
  // Override (part of) the data for a spell. These take effect regardless of how the spell is prepared.
  // For example, Illusionist wizards cast Minor Illusion as a bonus action: `overrides: { 'Minor Illusion': { castingTime: 'ba' } }`.
  // High-level rangers cast Hunter's Mark without concentration: `overrides: { 'Hunter\'s Mark': { concentration: false } }`.
  overrides: Record<string, Partial<SpellData>>
  // This expression is joined with the base expression for the spell save dc.
  bonusSaves?: DiceExpr
  // This expression is joined with the base expression for the spell attack modifier.
  bonusAttacks?: DiceExpr
  // Grants a number of additional spell slots. NB: 0-indexed - `[1, 0, 1]` gives one extra first and third level spell slot.
  bonusSlots?: number[]
  // The evaluated (constant) value is added to the number of spells that can be prepared
  bonusPreparations?: DiceExpr
  // The evaluated (constant) value is added to the number of cantrps that can be prepared
  bonusCantrips?: DiceExpr
  // Hide the spell slot counters
  hideSlots?: boolean
}

export type CharacterSheetData = BaseStats & {
  class: string
  subclass?: string
  race: string
  alignment?: string
  background?: string

  portrait?: string
  customSkills?: Record<string, DiceExpr>
  jackOfAllTrades?: boolean
  reliableTalent?: boolean
  customSaves?: Record<string, DiceExpr>
  toggles?: Record<string, Partial<CharacterSheetData>>
  spells: PlayerSpellcastingData
}

export const modifier = (attr: number): number => Math.floor((attr - 10) / 2)

export const totalLevel = (level: number | LevelData): number => {
  if (_.isNumber(level)) return level as number
  return Object.values(level).reduce((a, b) => a + b, 0)
}

export const getClassHitdie = (cls?: string): string | null => {
  const classes: Record<string, string> = {
    artificer: 'd8',
    bard: 'd8',
    barbarian: 'd12',
    cleric: 'd8',
    druid: 'd8',
    fighter: 'd10',
    monk: 'd8',
    ranger: 'd10',
    rogue: 'd8',
    paladin: 'd10',
    psion: 'd6',
    spellsword: 'd8',
    sorcerer: 'd6',
    warlock: 'd8',
    wizard: 'd6',
  }
  if (!cls) return null
  return classes[cls] || null
}

export function resolveMods(stats: BaseStats, extra: Record<string, DiceExpr> = {}): Record<string, DiceExpr> {
  if (!stats || !stats.abilities) return {}
  const abilities = _.mapValues(resolveAbilityScores(stats), modifier)
  const classLevels = _.isObject(stats.level) ? stats.level : stats.class ? { [stats.class]: stats.level } : {}

  const mods = {
    prof: proficiency(stats),
    level: totalLevel(stats.level),
    ...classLevels,
    ...abilities,
    ...(stats.mods || {}),
    ...extra,
  }

  return mods
}

export function proficiency(stats: BaseStats) {
  if (stats.prof) return stats.prof
  const level = stats.cr ? parseInt(stats.cr as string) || 1 : totalLevel(stats.level)
  return 2 + Math.floor((level - 1) / 4)
}

function normalizeSkillname(skill: string) {
  return skill?.toLowerCase()?.replaceAll(' ', '')
}

export function skillExpr(stats: any, skill: string, passive: boolean = false): string {
  const skillNorm = normalizeSkillname(skill)
  const pass = passive ? '10' : ''
  const stat = skillStats[skillNorm]
  const prof = (stats.skills || []).map(normalizeSkillname).includes(skillNorm) ? 'prof' : ''
  const exp = (stats.expertise || []).map(normalizeSkillname).includes(skillNorm) ? 'prof' : ''

  return joinDice(pass, stat, prof, exp) as string
}

export function resolveAbilityScores(stats: BaseStats): Record<Ability, number> {
  const { str, dex, con, int, wis, cha } = stats.abilities
  return {
    str: resolveDice(str, {}, true),
    dex: resolveDice(dex, {}, true),
    con: resolveDice(con, {}, true),
    int: resolveDice(int, {}, true),
    wis: resolveDice(wis, {}, true),
    cha: resolveDice(cha, {}, true),
  }
}

export const emitRest = (...rests: string[]) => {
  rests.forEach((rest) => {
    const event = new CustomEvent('CounterReset', { detail: { type: rest } })
    document.body.dispatchEvent(event)
  })
}

export const statnames: Record<Ability, string> = {
  str: 'Strength',
  dex: 'Dexterity',
  con: 'Constitution',
  int: 'Intelligence',
  wis: 'Wisdom',
  cha: 'Charisma',
}

export const skillStats: Record<string, Ability> = {
  athletics: 'str',
  acrobatics: 'dex',
  stealth: 'dex',
  'sleight of hand': 'dex',
  arcana: 'int',
  history: 'int',
  investigation: 'int',
  nature: 'int',
  religion: 'int',
  insight: 'wis',
  'animal handling': 'wis',
  medicine: 'wis',
  perception: 'wis',
  survival: 'wis',
  intimidation: 'cha',
  deception: 'cha',
  persuasion: 'cha',
  performance: 'cha',
}

export const hitDice: Record<string, string> = {
  tiny: 'd4',
  small: 'd6',
  medium: 'd8',
  large: 'd10',
  huge: 'd12',
  gargantuan: 'd20',
}

export const alignments: Record<string, string> = {
  lg: 'lawful good',
  ng: 'neutral good',
  cg: 'chaotic good',
  ln: 'lawful neutral',
  tn: 'true neutral',
  cn: 'chaotic neutral',
  le: 'lawful evil',
  ne: 'neutral evil',
  ce: 'chaotic evil',
}

export const resolveSpeed = (spd?: string | SpeedData, exhaustion: number = 0) => {
  if (!spd) return null
  const exh = exhaustion * 5
  const movementText = () => {
    if (spd.constructor === String) return spd
    const speed = spd as SpeedData
    const walk = speed.walk ? `${speed.walk - exh}ft` : null
    const climb = speed.climb ? `${speed.climb - exh}ft climb` : null
    const swim = speed.swim ? `${speed.swim - exh}ft swim` : null
    const flight = speed.fly ? `${speed.fly - exh}ft fly` : null

    return [walk, climb, swim, flight].filter((x) => !!x).join(', ')
  }
  const label = movementText()
  // should this be the highest value instead?
  if (spd.constructor === String)
    return [
      {
        name: 'speed',
        icon: 'game-icons:run',
        value: spd,
        label,
      },
    ]

  const res = []
  const speed = spd as SpeedData
  res.push({
    name: 'speed',
    icon: 'game-icons:run',
    value: (speed.walk || 30) - exh,
    label,
  })
  if (speed.fly)
    res.push({
      name: 'fly',
      icon: 'game-icons:curly-wing',
      value: speed.fly - exh,
      label,
    })
  if (speed.climb)
    res.push({
      name: 'climb',
      icon: 'game-icons:squirrel',
      value: speed.climb - exh,
      label,
    })
  if (speed.swim)
    res.push({
      name: 'swim',
      icon: 'game-icons:shark-fin',
      value: speed.swim - exh,
      label,
    })

  return res
}

export const resolveSenses = (senses?: SenseData) => {
  const resolveLabel = () => {
    if (!senses) return 'No special senses'
    const darkvision = senses.dark ? `${senses.dark}ft darkvision` : null
    const blindsight = senses.blind ? `${senses.blind}ft blindsight` : null
    const tremorsense = senses.tremor ? `${senses.tremor}ft tremorsense` : null
    const truesight = senses.true ? `${senses.true}ft truesight` : null
    return [darkvision, blindsight, tremorsense, truesight].filter((x) => !!x).join(', ')
  }

  const noVision = {
    name: 'none',
    value: 0,
    icon: 'fa6-solid:eye',
    label: null,
  }

  if (!senses) return noVision

  const label = resolveLabel()

  if (senses.true)
    return {
      name: 'true',
      value: senses.true,
      icon: 'game-icons:sheikah-eye',
      label,
    }
  if (senses.blind)
    return {
      name: 'blind',
      value: senses.blind,
      icon: 'game-icons:radar-sweep',
      label,
    }
  if (senses.tremor)
    return {
      name: 'tremor',
      value: senses.tremor,
      icon: 'game-icons:dig-hole',
      label,
    }
  if (senses.dark)
    return {
      name: 'dark',
      value: senses.dark,
      icon: 'game-icons:semi-closed-eye',
      label,
    }
  return noVision
}

export default modifier
